import React from 'react';

type RatingSource = {
  id: string;
  tournamentName: string;
  level: string;
  points: number;
  category?: string | null;
  stageName?: string | null;
  stageCode?: string | null;
  awardedAt?: string | null;
  expiresAt?: string | null;
};

type RatingItem = {
  id: string;
  level: string;
  playerLevel?: string | null;
  position: number;
  totalPoints: number;
  tournamentsPlayed: number;
  grandSlamPoints: number;
  mastersPoints: number;
  finalsPoints: number;
  cup500Points: number;
  cup250Points: number;
  matchesPlayed: number;
  firstMatchAt?: string | null;
  setDiff: number;
  gameDiff: number;
  player: {
    id: string;
    name: string;
  };
  sources?: RatingSource[];
};

type RatingDocument = {
  id: string;
  ratingType: 'TTM_CUP' | 'TTM_DOUBLES' | string;
  status: 'DRAFT' | 'POSTED' | string;
  accrualDate?: string | null;
  weekKey: string;
  itemsCount: number;
  totalPoints: number;
  notes?: string | null;
  createdAt: string;
  items: RatingItem[];
};

type Props = {
  open: boolean;
  onClose: () => void;
  document: RatingDocument | null;
  onRecalculate?: () => void | Promise<void>;
  onSaveRecalculation?: () => void | Promise<void>;
  showSaveButton?: boolean;
  recalculateLoading?: boolean;
  saveLoading?: boolean;
  onChangeAccrualDate?: (nextIsoDate: string) => void | Promise<void>;
  accrualDateSaving?: boolean;
};

const getRatingTypeLabel = (type?: string) => {
  if (type === 'TTM_CUP') return 'TTM CUP';
  if (type === 'TTM_DOUBLES') return 'TTM DOUBLES';
  return type || '-';
};

const formatSourceSubtitle = (source: RatingSource) => {
  const parts: string[] = [];
  if (source.level) parts.push(source.level);
  if (source.stageName) parts.push(source.stageName);
  return parts.join(' • ');
};

const RatingPointsTooltip: React.FC<{ item: RatingItem }> = ({ item }) => {
  if (!item.sources?.length) return null;

  return (
    <div className="pointer-events-none invisible opacity-0 group-hover:visible group-hover:opacity-100 transition-opacity absolute right-0 top-full mt-1 z-20 w-[360px] max-w-[70vw] rounded-md border border-gray-200 bg-white shadow-lg p-2 text-xs text-gray-700">
      <div className="font-semibold text-gray-900 mb-1">
        Состав очков: {item.totalPoints}
      </div>
      <div className="space-y-1.5">
        {item.sources.map((source) => (
          <div key={source.id} className="border-b border-gray-100 pb-1 last:border-b-0">
            <div className="flex items-start justify-between gap-2">
              <span className="text-gray-900">{source.tournamentName}</span>
              <span className="font-semibold">{source.points}</span>
            </div>
            {formatSourceSubtitle(source) && (
              <div className="text-gray-500">{formatSourceSubtitle(source)}</div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

const RatingDocumentModal: React.FC<Props> = ({
  open,
  onClose,
  document,
  onRecalculate,
  onSaveRecalculation,
  showSaveButton = false,
  recalculateLoading = false,
  saveLoading = false,
  onChangeAccrualDate,
  accrualDateSaving = false
}) => {
  if (!open || !document) return null;

  const statusLabel = document.status === 'DRAFT' ? 'Черновик' : document.status === 'POSTED' ? 'Проведен' : document.status;

  const sortedItems = React.useMemo(() => {
    const arr = [...(document.items || [])];
    arr.sort((a, b) => {
      if (a.level !== b.level) return a.level.localeCompare(b.level, 'ru');
      if (a.position !== b.position) return a.position - b.position;
      if (a.totalPoints !== b.totalPoints) return b.totalPoints - a.totalPoints;
      return String(a.player?.name || '').localeCompare(String(b.player?.name || ''), 'ru');
    });
    return arr;
  }, [document.items]);

  const accrualDateValue = React.useMemo(() => {
    const source = document.accrualDate || document.createdAt;
    const d = new Date(source);
    if (Number.isNaN(d.getTime())) return '';
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }, [document.accrualDate, document.createdAt]);

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl max-h-[90vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="text-base font-semibold text-gray-900">Документ рейтинга</h3>
            <p className="text-xs text-gray-500">
              Тип: {getRatingTypeLabel(document.ratingType)} | Неделя: {document.weekKey}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {showSaveButton && onSaveRecalculation && (
              <button
                type="button"
                onClick={onSaveRecalculation}
                disabled={saveLoading || recalculateLoading}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
              >
                {saveLoading ? 'Сохранение...' : 'Сохранить'}
              </button>
            )}
            {onRecalculate && (
              <button
                type="button"
                onClick={onRecalculate}
                disabled={recalculateLoading || saveLoading}
                className="px-3 py-1.5 text-sm bg-amber-100 text-amber-800 rounded hover:bg-amber-200 disabled:opacity-60"
              >
                {recalculateLoading ? 'Пересчет...' : 'Пересчет'}
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded hover:bg-gray-200"
            >
              Закрыть
            </button>
          </div>
        </div>

        <div className="p-4 space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-6 gap-3 text-sm">
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Статус</div>
              <div className="font-medium">{statusLabel}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Строк</div>
              <div className="font-medium">{document.itemsCount}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Сумма очков</div>
              <div className="font-medium">{document.totalPoints}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Дата создания</div>
              <div className="font-medium">{new Date(document.createdAt).toLocaleString('ru-RU')}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs">Неделя</div>
              <div className="font-medium">{document.weekKey}</div>
            </div>
            <div className="bg-gray-50 rounded p-2">
              <div className="text-gray-500 text-xs mb-1">Дата расчета</div>
              {document.status === 'DRAFT' && onChangeAccrualDate ? (
                <input
                  type="date"
                  value={accrualDateValue}
                  onChange={(e) => onChangeAccrualDate(e.target.value)}
                  disabled={accrualDateSaving}
                  className="border border-gray-300 rounded px-2 py-1 text-sm"
                />
              ) : (
                <div className="font-medium">{new Date(document.accrualDate || document.createdAt).toLocaleDateString('ru-RU')}</div>
              )}
            </div>
          </div>

          <div className="overflow-auto border border-gray-200 rounded max-h-[60vh]">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">Игрок</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-left font-medium text-gray-600">Уровень</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Позиция</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Очки</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Турниры</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Матчи</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">GS</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">M</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">F</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">500</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">250</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Δ сет</th>
                  <th className="sticky top-0 z-10 bg-gray-50 px-3 py-2 text-right font-medium text-gray-600">Δ гейм</th>
                </tr>
              </thead>
              <tbody>
                {sortedItems.length === 0 ? (
                  <tr>
                    <td colSpan={13} className="px-3 py-8 text-center text-gray-500">
                      Для этого документа не найдено строк рейтинга.
                    </td>
                  </tr>
                ) : (
                  sortedItems.map((item) => (
                    <tr key={item.id} className="border-t border-gray-100">
                      <td className="px-3 py-2">
                        <span>{`${item.player?.name || item.player?.id} (${item.playerLevel || item.level})`}</span>
                      </td>
                      <td className="px-3 py-2">{item.level}</td>
                      <td className="px-3 py-2 text-right">{item.position}</td>
                      <td className="px-3 py-2 text-right font-semibold">
                        <div className="relative inline-block group">
                          <span className={item.sources?.length ? 'cursor-help border-b border-dotted border-gray-400' : ''}>
                            {item.totalPoints}
                          </span>
                          <RatingPointsTooltip item={item} />
                        </div>
                      </td>
                      <td className="px-3 py-2 text-right">{item.tournamentsPlayed}</td>
                      <td className="px-3 py-2 text-right">{item.matchesPlayed}</td>
                      <td className="px-3 py-2 text-right">{item.grandSlamPoints}</td>
                      <td className="px-3 py-2 text-right">{item.mastersPoints}</td>
                      <td className="px-3 py-2 text-right">{item.finalsPoints}</td>
                      <td className="px-3 py-2 text-right">{item.cup500Points}</td>
                      <td className="px-3 py-2 text-right">{item.cup250Points}</td>
                      <td className="px-3 py-2 text-right">{item.setDiff}</td>
                      <td className="px-3 py-2 text-right">{item.gameDiff}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RatingDocumentModal;

